import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Ticket,
  Plus,
  Search,
  Mail,
  Phone,
  BookOpen,
  MessageCircle,
  Clock,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Loader2,
  ChevronRight,
  Send,
  Paperclip,
  Eye,
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const prioridadeOptions = [
  { value: 'baixa', label: 'Baixa', color: 'bg-gray-500' },
  { value: 'media', label: 'Média', color: 'bg-blue-500' },
  { value: 'alta', label: 'Alta', color: 'bg-orange-500' },
  { value: 'urgente', label: 'Urgente', color: 'bg-red-500' },
];

const statusLabels = {
  aberto: { label: 'Aberto', color: 'bg-blue-500', icon: AlertCircle },
  em_atendimento: { label: 'Em Atendimento', color: 'bg-yellow-500', icon: Loader2 },
  aguardando_resposta: { label: 'Aguardando Resposta', color: 'bg-purple-500', icon: Clock },
  resolvido: { label: 'Resolvido', color: 'bg-green-500', icon: CheckCircle },
  fechado: { label: 'Fechado', color: 'bg-gray-500', icon: CheckCircle },
};

const Suporte = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [showNovoTicket, setShowNovoTicket] = useState(false);
  const [showDetalhes, setShowDetalhes] = useState(false);
  const [ticketSelecionado, setTicketSelecionado] = useState(null);
  const [novaMensagem, setNovaMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);
  
  const [novoTicket, setNovoTicket] = useState({
    titulo: '',
    descricao: '',
    categoria_id: '',
    prioridade: 'media',
  });

  const token = localStorage.getItem('agrolink_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ticketsRes, categoriasRes] = await Promise.all([
        axios.get(`${API_URL}/api/tickets`, { headers }),
        axios.get(`${API_URL}/api/ticket-categorias`, { headers }),
      ]);
      setTickets(ticketsRes.data);
      setCategorias(categoriasRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCriarTicket = async () => {
    if (!novoTicket.titulo || !novoTicket.descricao) {
      toast.error('Preencha o título e a descrição');
      return;
    }

    try {
      setEnviando(true);
      await axios.post(`${API_URL}/api/tickets`, novoTicket, { headers });
      toast.success('Ticket criado com sucesso! Em breve entraremos em contato.');
      setShowNovoTicket(false);
      setNovoTicket({ titulo: '', descricao: '', categoria_id: '', prioridade: 'media' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar ticket');
    } finally {
      setEnviando(false);
    }
  };

  const handleVerDetalhes = async (ticket) => {
    try {
      const res = await axios.get(`${API_URL}/api/tickets/${ticket.id}`, { headers });
      setTicketSelecionado(res.data);
      setShowDetalhes(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleEnviarMensagem = async () => {
    if (!novaMensagem.trim()) return;

    try {
      setEnviando(true);
      const formData = new FormData();
      formData.append('mensagem', novaMensagem);
      
      await axios.post(`${API_URL}/api/tickets/${ticketSelecionado.id}/mensagens`, formData, { headers });
      toast.success('Mensagem enviada');
      setNovaMensagem('');
      
      // Recarregar ticket
      const res = await axios.get(`${API_URL}/api/tickets/${ticketSelecionado.id}`, { headers });
      setTicketSelecionado(res.data);
      fetchData();
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
    } finally {
      setEnviando(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Central de Suporte</h1>
          <p className="text-muted-foreground">Como podemos ajudar você hoje?</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card 
          className="cursor-pointer hover:shadow-lg transition-all border-2 border-primary/20 hover:border-primary/50"
          onClick={() => setShowNovoTicket(true)}
        >
          <CardContent className="p-6 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Plus className="w-8 h-8 text-primary" />
            </div>
            <h3 className="font-semibold text-lg">Abrir Ticket</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Relate um problema ou dúvida
            </p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-all"
          onClick={() => document.getElementById('meus-tickets')?.scrollIntoView({ behavior: 'smooth' })}
        >
          <CardContent className="p-6 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mb-4">
              <Search className="w-8 h-8 text-blue-500" />
            </div>
            <h3 className="font-semibold text-lg">Acompanhar Ticket</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Veja o status dos seus tickets
            </p>
            {tickets.filter(t => t.status !== 'fechado' && t.status !== 'resolvido').length > 0 && (
              <Badge className="mt-2" variant="secondary">
                {tickets.filter(t => t.status !== 'fechado' && t.status !== 'resolvido').length} em aberto
              </Badge>
            )}
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-all"
          onClick={() => navigate('/base-conhecimento')}
        >
          <CardContent className="p-6 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-4">
              <BookOpen className="w-8 h-8 text-emerald-500" />
            </div>
            <h3 className="font-semibold text-lg">Base de Conhecimento</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Artigos e tutoriais
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Contact Info */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Mail className="w-5 h-5" />
              <a href="mailto:suporte@agrolinkbr.com.br" className="hover:text-primary transition-colors">
                suporte@agrolinkbr.com.br
              </a>
            </div>
            <div className="hidden sm:block w-px h-6 bg-border" />
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="w-5 h-5" />
              <span>Seg-Sex: 8h às 18h</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Meus Tickets */}
      <Card id="meus-tickets">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ticket className="w-5 h-5" />
            Meus Tickets
          </CardTitle>
          <CardDescription>Acompanhe o status dos seus tickets de suporte</CardDescription>
        </CardHeader>
        <CardContent>
          {tickets.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Ticket className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Você ainda não abriu nenhum ticket</p>
              <Button className="mt-4" onClick={() => setShowNovoTicket(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Abrir Primeiro Ticket
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ticket</TableHead>
                  <TableHead className="hidden sm:table-cell">Categoria</TableHead>
                  <TableHead className="hidden md:table-cell">Prioridade</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden lg:table-cell">Data</TableHead>
                  <TableHead className="w-20">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.map((ticket) => {
                  const status = statusLabels[ticket.status] || statusLabels.aberto;
                  const StatusIcon = status.icon;
                  const prioridade = prioridadeOptions.find(p => p.value === ticket.prioridade);
                  
                  return (
                    <TableRow key={ticket.id} className="cursor-pointer hover:bg-muted/50" onClick={() => handleVerDetalhes(ticket)}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{ticket.titulo}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {ticket.descricao}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {ticket.categoria_nome || '-'}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <Badge className={cn('text-white', prioridade?.color)}>
                          {prioridade?.label || ticket.prioridade}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={cn('gap-1 text-white', status.color)}>
                          <StatusIcon className="w-3 h-3" />
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                        {formatDate(ticket.created_at)}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog Novo Ticket */}
      <Dialog open={showNovoTicket} onOpenChange={setShowNovoTicket}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Abrir Novo Ticket</DialogTitle>
            <DialogDescription>
              Descreva seu problema ou dúvida e nossa equipe entrará em contato
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input
                placeholder="Resumo do problema..."
                value={novoTicket.titulo}
                onChange={(e) => setNovoTicket({ ...novoTicket, titulo: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select
                  value={novoTicket.categoria_id}
                  onValueChange={(v) => setNovoTicket({ ...novoTicket, categoria_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categorias.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Prioridade</Label>
                <Select
                  value={novoTicket.prioridade}
                  onValueChange={(v) => setNovoTicket({ ...novoTicket, prioridade: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {prioridadeOptions.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        <div className="flex items-center gap-2">
                          <div className={cn('w-2 h-2 rounded-full', p.color)} />
                          {p.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Descrição *</Label>
              <Textarea
                placeholder="Descreva detalhadamente o problema ou dúvida..."
                rows={5}
                value={novoTicket.descricao}
                onChange={(e) => setNovoTicket({ ...novoTicket, descricao: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovoTicket(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCriarTicket} disabled={enviando}>
              {enviando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Detalhes do Ticket */}
      <Dialog open={showDetalhes} onOpenChange={setShowDetalhes}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          {ticketSelecionado && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle>{ticketSelecionado.titulo}</DialogTitle>
                    <DialogDescription className="mt-1">
                      Aberto em {formatDate(ticketSelecionado.created_at)}
                    </DialogDescription>
                  </div>
                  <Badge className={cn('text-white', statusLabels[ticketSelecionado.status]?.color)}>
                    {statusLabels[ticketSelecionado.status]?.label}
                  </Badge>
                </div>
              </DialogHeader>
              
              <div className="flex-1 overflow-y-auto space-y-4 py-4">
                {/* Descrição original */}
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm whitespace-pre-wrap">{ticketSelecionado.descricao}</p>
                </div>
                
                {/* Mensagens */}
                {ticketSelecionado.mensagens?.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-sm text-muted-foreground">Conversas</h4>
                    {ticketSelecionado.mensagens.map((msg) => (
                      <div
                        key={msg.id}
                        className={cn(
                          'p-3 rounded-lg',
                          msg.is_admin ? 'bg-primary/10 ml-4' : 'bg-muted mr-4'
                        )}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">
                            {msg.is_admin ? '🛡️ Suporte' : msg.usuario_nome}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(msg.created_at)}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{msg.mensagem}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Input de mensagem */}
              {ticketSelecionado.status !== 'fechado' && ticketSelecionado.status !== 'resolvido' && (
                <div className="border-t pt-4">
                  <div className="flex gap-2">
                    <Textarea
                      placeholder="Digite sua mensagem..."
                      value={novaMensagem}
                      onChange={(e) => setNovaMensagem(e.target.value)}
                      rows={2}
                      className="flex-1"
                    />
                    <Button onClick={handleEnviarMensagem} disabled={enviando || !novaMensagem.trim()}>
                      {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Suporte;
